import { format, parseISO } from 'date-fns';

const STATUS = {
  INACTIVE: 'inactive',
  PENDING: 'pending',
  RECEIVED: 'order received',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  CANCELED: 'canceled',
};

const STATUS_BY_ORDER_TASK_STATUS = {
  canceled: STATUS.CANCELED,
  cancelled: STATUS.CANCELED,
  declined: STATUS.PENDING,
  incomplete: STATUS.RECEIVED,
  pending: STATUS.RECEIVED,
  processing: STATUS.PENDING,
  action_req: STATUS.PENDING,
  active: STATUS.ACTIVE,
  complete: STATUS.COMPLETE,
};

export const getDerivedStatus = (status) => (status ? STATUS_BY_ORDER_TASK_STATUS[status] : STATUS.INACTIVE);

// A renewal row is dated by its own payment event. createdDatetime is fixed at the original
// purchase, so it is only a fallback for order tasks that have no payment event of their own.
function getRowDate(row) {
  return row?.paymentDate ?? row?.createdDatetime;
}

export function getOrderYear(obj) {
  const date = new Date(getRowDate(obj));
  return date.getFullYear();
}

export function getUniqueYears(rows) {
  const years = rows.map((row) => getOrderYear(row));
  const uniqueSet = new Set(years);
  return Array.from(uniqueSet).sort((a, b) => b - a);
}

function sortPlans(unsorted) {
  return unsorted.sort((a, b) => new Date(getRowDate(b)) - new Date(getRowDate(a)));
}

export function filterRowsByYear(rows, year) {
  if (year == null || year === '') {
    return rows;
  }
  return rows.filter((row) => getOrderYear(row) === year);
}

export function getFormattedDate(date) {
  return date ? format(parseISO(date), 'MMMM dd, yyyy') : 'Not Yet Available';
}

export function correctDisplaySource(task) {
  const values = {
    date: getFormattedDate(getRowDate(task)),
    price: task.amount,
    displayName: task.displayName,
  };

  if (task.orderTaskType === 'llc_formation') {
    return {
      ...values,
      price: task.fee != null ? `${task.fee}.00` : '0.00',
      displayName: `State Filing Fee - ${task.orderTaskJurisdiction}`,
    };
  }

  if (task.orderTaskType === 'registered_agent_service') {
    return { ...values, displayName: `${task.displayName} - ${task.orderTaskJurisdiction}` };
  }

  return values;
}

export const transactionalBundles = ['ein_business_docs_bundle'];

const includedInTransactionalBundles = new Map([['ein_business_docs_bundle', ['ein_creation', 'corporate_docs']]]);

export function filterTransactionBundles(orderTasks) {
  const result = [];
  const used = [];

  const bundles = orderTasks.filter((task) => transactionalBundles.includes(task.orderTaskType));
  if (bundles.length === 0) {
    return null;
  }

  bundles.forEach((bundle) => {
    const bundled = includedInTransactionalBundles.get(bundle.orderTaskType);
    const included = [];
    for (const item of bundled) {
      const ot = orderTasks.find((task) => task.orderTaskType === item);
      if (ot) {
        included.push(ot);
        used.push(ot);
      }
    }
    result.push({ ...bundle, included, isTransactional: true });
    used.push(bundle);
  });

  return { transactionalBundles: result, used };
}

function byPaymentDateAscending(a, b) {
  return new Date(a?.paymentDate ?? 0) - new Date(b?.paymentDate ?? 0);
}

export function groupPlanWithTasksByYear(orderTasks, paymentHistory) {
  const rows = [];
  const usedItemUuids = new Set();

  let remainingOrderTasks = orderTasks;
  const transactionalItems = filterTransactionBundles(orderTasks);

  if (transactionalItems && transactionalItems.transactionalBundles.length > 0) {
    const { transactionalBundles: bundles, used } = transactionalItems;
    bundles.forEach((bundle) => rows.push(bundle));
    used.forEach((task) => usedItemUuids.add(task?.orderItemUuid));

    remainingOrderTasks = orderTasks.filter(
      (task) => used.findIndex((item) => item?.orderTaskType === task?.orderTaskType) === -1,
    );
  }

  if (!paymentHistory || !Array.isArray(paymentHistory)) {
    return rows;
  }

  // One row per payment event, so a renewal is no longer collapsed into the original purchase.
  // Sorted oldest-first rather than trusting the API's order, so the earliest event for an item
  // is always its original purchase.
  const chargedPayments = paymentHistory
    .filter((payment) => ['price', 'fee'].includes(payment?.chargeType))
    .slice()
    .sort(byPaymentDateAscending);

  const claimedAsChildItemUuids = new Set();
  const emittedIncludedItemUuids = new Set();

  const addPaymentRow = (payment) => {
    const ot = remainingOrderTasks.find((item) => item?.orderItemUuid === payment?.orderItemUuid);
    if (!ot) {
      return;
    }

    usedItemUuids.add(ot.orderItemUuid);

    const { includedItems, ...paymentWithoutIncludedItems } = payment;
    const result = { ...ot, ...paymentWithoutIncludedItems };

    if (!includedItems) {
      rows.push(result);
      return;
    }

    result.included = [];

    includedItems.forEach((included) => {
      const task = remainingOrderTasks.find((t) => t?.orderItemUuid === included?.orderItemUuid);
      if (!task) {
        return;
      }
      usedItemUuids.add(task.orderItemUuid);
      claimedAsChildItemUuids.add(task.orderItemUuid);

      if (task.orderTaskType === 'llc_formation') {
        // The state filing fee is charged once at formation, so emit it once however many
        // payment events carry it - a plan's renewals and sibling plan items all include it.
        if (!emittedIncludedItemUuids.has(task.orderItemUuid)) {
          emittedIncludedItemUuids.add(task.orderItemUuid);
          rows.push({ ...task, ...included });
        }
        return;
      }

      result.included.push({ ...task });
    });

    rows.push(result);
  };

  // Plans claim their included items before standalone payments are considered. An included item
  // usually also has a payment event of its own, and without this ordering that event emits a
  // duplicate top-level row for something already shown under its plan.
  chargedPayments.filter((payment) => payment.includedItems).forEach(addPaymentRow);

  chargedPayments
    .filter((payment) => !payment.includedItems)
    .forEach((payment) => {
      if (claimedAsChildItemUuids.has(payment?.orderItemUuid)) {
        return;
      }
      addPaymentRow(payment);
    });

  orderTasks.forEach((task) => {
    if (!usedItemUuids.has(task?.orderItemUuid)) {
      rows.push(task);
    }
  });

  return sortPlans(rows);
}
